import { useToast } from '@chakra-ui/react';
import { QueryKey, useQuery } from '@tanstack/react-query';
import { ApiClient, useApiClient } from 'api/context-provider/useApiClient';
import { ApiError, InferFetchData, throwOnError } from 'api/helpers';
import { useEffect } from 'react';

type Params = {
  page?: number;
  itemsPerPage?: number;
};

async function getAutomationActivityLog(apiClient: ApiClient, params: Params) {
  return apiClient.GET('/op/v2/automations/activity-log', { params: { query: params } });
}

export type AutomationActivityLogResponse = InferFetchData<typeof getAutomationActivityLog>;

function getKey(page?: number, itemsPerPage?: number) {
  return ['AutomationActivityLog', page, itemsPerPage] as const satisfies QueryKey;
}

export default function useAutomationActivityLogQuery({ page = 1, itemsPerPage = 20 }: Params = {}) {
  const apiClient = useApiClient();
  const toast = useToast();

  const query = useQuery<AutomationActivityLogResponse, ApiError>({
    queryKey: getKey(page, itemsPerPage),
    queryFn: () => throwOnError(getAutomationActivityLog(apiClient, { page, itemsPerPage })),
  });

  useEffect(() => {
    if (query.isError) {
      toast({ title: query.error.message, status: 'error' });
    }
  }, [query.error, query.isError, toast]);

  return query;
}

useAutomationActivityLogQuery.getKey = getKey;
